import { skipToken, useQuery } from "@tanstack/react-query";
import {
  fetchChatToken,
  type ChatTokenData,
} from "Api/Queries/Chat/useChatTokenQuery";
import {
  ChatConnectSupersededError,
  connectStreamChat,
} from "Utils/Chat/streamChatClient";
import { isConnectSupersededError } from "Utils/ConnectSupersededError";

const STREAM_CHAT_CLIENT_QUERY_KEY = "STREAM_CHAT_CLIENT";

export const streamChatClientKeys = {
  all: [STREAM_CHAT_CLIENT_QUERY_KEY],
  byPersona: (apiKey?: string, userId?: string) => [
    ...streamChatClientKeys.all,
    apiKey,
    userId,
  ],
};

/**
 * The one way to obtain the connected Stream Chat client — the chat page and the unread badge
 * share the module-singleton connection behind it. The connection lives in a React Query query so
 * transient connect failures retry with backoff (and an errored query re-fetches on the next
 * window focus/remount), and a persona switch synchronously resets `data` to undefined — no frame
 * renders the previous persona's client. A superseded connect is a cancellation that
 * `reportApiError` drops; a real connect failure is reported to Sentry.
 */
export const useStreamChatClient = (chatToken: ChatTokenData | undefined) => {
  const { data: client, isPending } = useQuery({
    // Keyed by persona, NOT by token string — a token refetch must not reconnect the socket;
    // token freshness is the token provider's job.
    queryKey: streamChatClientKeys.byPersona(
      chatToken?.apiKey,
      chatToken?.userId,
    ),
    queryFn: chatToken
      ? ({ signal }) =>
          connectStreamChat(
            chatToken,
            async () => {
              // Fresh token each call — the SDK invokes this on connect AND on expiry refresh.
              // Never hand a foreign persona's token to this connection: if the active
              // IdentityUser changed under us, the key change drives the reconnect instead —
              // this connect is superseded, not failed.
              const fresh = await fetchChatToken();
              if (fresh.userId !== chatToken.userId) {
                throw new ChatConnectSupersededError(
                  "Chat token persona changed",
                );
              }
              return fresh.token;
            },
            signal,
          )
      : skipToken,
    // A resolved client never goes stale — the WebSocket keeps it live; the module singleton in
    // streamChatClient is the real cache, so the query entry itself can be dropped when unused.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    // The client is a live class instance — React Query must not walk/replace its object graph.
    structuralSharing: false,
    // Cause-aware: the SDK rewraps a token-provider throw, see isConnectSupersededError.
    retry: (failureCount, error) =>
      !isConnectSupersededError(error) && failureCount < 3,
  });

  return { client, isPending };
};
